/**
 * ConflictMediationBriefPage — generation form + rendered Brief.
 *
 * Architecturally still a mode_variant on Pre-Meeting Brief
 * (blueprint_type='pre_meeting', mode_variant='conflict_mediation') per
 * /shared/V1.x_Conflict_Mediation_Brief_Spec.md — but the **delivery
 * tier** moved from Tier 1 streaming to Tier 2 background+polling on
 * 2026-05-09 (see /shared/V1_CM_Tier2_Migration_Addendum.md). Function
 * logs proved CM was hitting Netlify's V2 streaming runtime cap
 * (Duration: 60367 ms), killing the function mid-generation.
 *
 * Body shape submitted to /api/conflict-mediation/start:
 *   {
 *     parties: [{ profile_id, label: "party_a" }, { profile_id, label: "party_b" }],
 *     meeting: { scheduled_at_iso, duration_minutes, title, trigger, history, desired_outcome },
 *     tone: "hard" | "balanced" | "soft"
 *   }
 *
 * The page renders BlueprintJobView during generation (4-item Tier 2
 * progress legibility — timer, phase, under-promised estimate, cancel)
 * and the inlined blueprint row from the polling endpoint when status
 * flips to complete.
 *
 * UI follows the locked v10 grammar and the design recommendations at
 * /shared/V1.x_Conflict_Mediation_Design_Recommendations.md (mediator
 * vantage in copy, hedge vocabulary, "preparation tool, not professional
 * services" lane held).
 */

import { useState, useRef, useEffect, startTransition } from "react";
import { useSession } from "../lib/useSession.js";
import { diag } from "../lib/diag.js";
import {
  Wordmark, SectionHeader, Callout, SeriesBanner, AuditFooter,
  MarkdownV2, Chip, BlueprintJobView, PrintToPdfButton, RecentBlueprintsPanel,
} from "../components/index.js";
import MentionAutocomplete from "../components/MentionAutocomplete.jsx";
import { canAccess, tierDisplayLabel } from "../lib/feature-gates.js";
import { parseJsonResponse } from "../lib/api-fetch.js";
import { terminalActionButtonStyle, terminalActionButtonDisabledStyle } from "../lib/buttonStyles.js";

export default function ConflictMediationBriefPage() {
  const { session, appUser } = useSession();

  // Two parties — each is a single-profile mention surface.
  const [partyAText, setPartyAText] = useState("");
  const [partyAMentions, setPartyAMentions] = useState([]);
  const [partyBText, setPartyBText] = useState("");
  const [partyBMentions, setPartyBMentions] = useState([]);

  // Mediation context
  const [trigger, setTrigger] = useState("");
  const [history, setHistory] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [tone, setTone] = useState("balanced");
  const [title, setTitle] = useState("");
  // Scheduled time + duration. **Optional** as of 2026-05-09 (option B
  // re-lock — see /shared/V1_CM_Optional_Scheduled_Time_Relock.md).
  // Originally added as required in bug #58 (2026-05-08) to kill the
  // lying-header bug (hardcoded `now + 4h / 60min`). Russ's product call
  // 2026-05-09: requiring fake data for users who haven't scheduled yet
  // recreates a softer version of the same bug. Optional with null
  // pass-through to the substrate is the honest path; the substrate
  // prompt is being taught (separate brainstorming-chat re-lock) to
  // render "Not yet scheduled" in the header line when the field is null.
  const [scheduledAt, setScheduledAt] = useState(""); // datetime-local string, "" = not picked
  const [durationMinutes, setDurationMinutes] = useState(""); // "" = not picked

  // Tier 2 (background+polling) per /shared/V1_CM_Tier2_Migration_Addendum.md
  // (locked 2026-05-09). Replaced Tier 1 streaming via useStreamingBlueprint
  // because pre-meeting-stream.js was hitting Netlify's V2 streaming runtime
  // cap (Duration: 60367 ms in function logs on 2026-05-09) — same
  // architectural ceiling that forced OM + Team to Tier 2 on 2026-05-07.
  // The page now renders BlueprintJobView during generation; the Tier 2
  // start endpoint kicks off a Background Function (15-min cap) and the
  // job state machine drives polling until complete.
  const [jobPayload, setJobPayload] = useState(null);  // payload submitted to /api/conflict-mediation/start
  const [blueprint, setBlueprint] = useState(null);
  const [localError, setLocalError] = useState(null);

  const tier = appUser?.tier || "manager";

  // Tier gate — Manager+ only (Recruiter explicitly excluded per spec).
  const tierAllowed = canAccess(tier, "pre_meeting_conflict_mediation");

  const generate = async () => {
    if (partyAMentions.length === 0 || partyBMentions.length === 0) {
      setLocalError("Mention both parties with @ to ground the brief in their Profiles. The third-party mediator vantage requires two named parties.");
      return;
    }
    if (partyAMentions.length > 1 || partyBMentions.length > 1) {
      setLocalError("Each party field accepts exactly one person. Remove the extra @ mention(s) — the brief is dyadic by design.");
      return;
    }
    if (partyAMentions[0].profile_id === partyBMentions[0].profile_id) {
      setLocalError("Party A and Party B must be different people.");
      return;
    }
    // Date + duration are optional per option-B re-lock (2026-05-09). If
    // the user provides duration, it must still be in bounds — but an
    // empty value is a valid "not yet scheduled" signal that flows to
    // the backend as null.
    let durationNum = null;
    if (durationMinutes !== "") {
      durationNum = parseInt(durationMinutes, 10);
      if (!durationNum || durationNum < 10 || durationNum > 240) {
        setLocalError("Duration must be between 10 and 240 minutes (or leave blank if not yet scheduled).");
        return;
      }
    }
    setLocalError(null);

    // Tier 2: stash the payload in state — BlueprintJobView mounts on the
    // next render and POSTs it to /api/conflict-mediation/start via its
    // `start` callback. The job_id returned drives polling.
    setJobPayload({
      meeting: {
        // null when the user hasn't picked yet — the substrate handles
        // the absence honestly. Bug #58 fix kept: no hardcoded placeholders.
        scheduled_at_iso: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        duration_minutes: durationNum,
        title: title.trim() || `Mediation · ${partyAMentions[0].display} × ${partyBMentions[0].display}`,
        trigger,
        history,
        desired_outcome: desiredOutcome,
      },
      parties: [
        { profile_id: partyAMentions[0].profile_id, label: "party_a" },
        { profile_id: partyBMentions[0].profile_id, label: "party_b" },
      ],
      tone,
    });
  };

  // Reset the in-flight job back to the form (used by the BlueprintJobView
  // cancel path so the user can try again with the same inputs).
  const resetJob = () => {
    setJobPayload(null);
    setBlueprint(null);
    setLocalError(null);
  };

  // Retry the same job payload — used by the Regenerate button in the
  // BlueprintJobView failure state (#72). Cycles jobPayload through null
  // → original payload so BlueprintJobView unmounts + remounts (which
  // re-fires its start() effect, enqueueing a new job_id). Mirrors the
  // streaming retry pattern from #62.
  const retryJob = () => {
    if (!jobPayload) return;
    const payload = jobPayload;
    setJobPayload(null);
    setLocalError(null);
    setTimeout(() => setJobPayload(payload), 0);
  };

  // Open a saved CM brief by id (RecentBlueprintsPanel onOpen handler, #78).
  // Fetches the full row + hydrates local blueprint state — same shape
  // BlueprintJobView's onComplete uses, so RenderedBrief renders correctly.
  const openSavedBlueprint = async (row) => {
    try {
      const res = await fetch(`/.netlify/functions/blueprint-by-id?id=${encodeURIComponent(row.blueprint_id)}`, {
        headers: { authorization: `Bearer ${session.access_token}` },
      });
      const body = await res.json();
      if (!res.ok) {
        setLocalError(body.error || `Failed to open saved brief (HTTP ${res.status})`);
        return;
      }
      startTransition(() => {
        setBlueprint({
          blueprint_id: body.blueprint_id,
          blueprint_type: "pre_meeting",
          mode_variant: "conflict_mediation",
          blueprint_markdown: body.blueprint_markdown,
          hedge_phrases: body.hedge_phrases || [],
          jargon_scan_passed: body.jargon_scan_passed,
          engine_version: body.engine_version,
        });
      });
    } catch (e) {
      setLocalError(`Failed to open saved brief: ${e.message}`);
    }
  };

  if (!session) {
    return <p style={{ padding: 24 }}>Sign in to generate a Conflict Mediation Brief.</p>;
  }

  if (!tierAllowed) {
    return (
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 24px" }}>
        <div style={{ marginBottom: 24 }}><Wordmark scale={1.0}/></div>
        <SectionHeader index={1} title="Conflict Mediation Brief"/>
        <Callout.WatchOut>
          <strong>Available on the Pro tier.</strong> Conflict Mediation Briefs are tier-gated
          to Pro and above. If you've
          landed here from a tile that should have been hidden, that's a bug — please flag it.
          {" "}<em>Your current tier: {tierDisplayLabel(tier)}.</em>
        </Callout.WatchOut>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Wordmark scale={1.0}/>
      </div>

      {jobPayload && !blueprint ? (
        <BlueprintJobView
          bearerToken={session.access_token}
          surfaceLabel="Conflict Mediation Brief"
          onRetry={retryJob}
          start={async () => {
            const res = await fetch("/.netlify/functions/conflict-mediation-start", {
              method: "POST",
              headers: {
                "content-type": "application/json",
                authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify(jobPayload),
            });
            const respBody = await parseJsonResponse(res);
            if (!res.ok) {
              return { ok: false, error: respBody.error || `Failed to start (HTTP ${res.status})` };
            }
            return {
              ok: true,
              job_id: respBody.job_id,
              estimated_total_seconds: respBody.estimated_total_seconds,
            };
          }}
          onComplete={(_blueprintId, jobRow) => {
            const bp = jobRow?.blueprint;
            diag("CM.onComplete", {
              has_blueprint: !!bp,
              blueprint_id: bp?.blueprint_id,
              markdown_chars: bp?.rendered_markdown?.length || 0,
              hedge_phrases_count: (bp?.hedge_phrases || []).length,
            });
            if (bp) {
              diag("CM.onComplete.setBlueprint.before");
              startTransition(() => {
                diag("CM.onComplete.setBlueprint.inside-transition");
                setBlueprint({
                  blueprint_id: bp.blueprint_id,
                  blueprint_type: "pre_meeting",
                  mode_variant: "conflict_mediation",
                  blueprint_markdown: bp.rendered_markdown,
                  hedge_phrases: bp.hedge_phrases || [],
                  jargon_scan_passed: bp.jargon_scan_passed,
                  engine_version: bp.layer_4_engine_version,
                });
              });
              diag("CM.onComplete.setBlueprint.after");
            }
          }}
        />
      ) : !blueprint ? (
        <>
          <RecentBlueprintsPanel
            type="pre_meeting"
            modeVariant="conflict_mediation"
            bearerToken={session.access_token}
            onOpen={openSavedBlueprint}
          />
          <GenerationForm
          partyAText={partyAText} setPartyAText={setPartyAText}
          partyAMentions={partyAMentions} setPartyAMentions={setPartyAMentions}
          partyBText={partyBText} setPartyBText={setPartyBText}
          partyBMentions={partyBMentions} setPartyBMentions={setPartyBMentions}
          trigger={trigger} setTrigger={setTrigger}
          history={history} setHistory={setHistory}
          desiredOutcome={desiredOutcome} setDesiredOutcome={setDesiredOutcome}
          tone={tone} setTone={setTone}
          title={title} setTitle={setTitle}
          scheduledAt={scheduledAt} setScheduledAt={setScheduledAt}
          durationMinutes={durationMinutes} setDurationMinutes={setDurationMinutes}
          generating={false}
          onSubmit={generate}
          error={localError}
          bearerToken={session.access_token}
        />
        </>
      ) : (
        <RenderedBrief
          blueprint={blueprint}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────

function GenerationForm({
  partyAText, setPartyAText, partyAMentions, setPartyAMentions,
  partyBText, setPartyBText, partyBMentions, setPartyBMentions,
  trigger, setTrigger,
  history, setHistory,
  desiredOutcome, setDesiredOutcome,
  tone, setTone,
  title, setTitle,
  scheduledAt, setScheduledAt,
  durationMinutes, setDurationMinutes,
  generating, onSubmit, error, bearerToken,
}) {
  return (
    <div>
      <SectionHeader index={1} title="New Conflict Mediation Brief"/>

      <Callout.Remember>
        This brief is written from the <strong>third-party mediator vantage</strong>. Mention the two parties
        you'll be mediating between — the brief will speak <em>about</em> them to <em>you</em> (the mediator),
        not to either of them directly. A reading, not a verdict. A starting frame, not a script.
      </Callout.Remember>

      <div style={{ display: "grid", gap: 16, marginTop: 20 }}>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Party A — mention one person with @">
            <MentionAutocomplete
              value={partyAText}
              onChange={setPartyAText}
              mentions={partyAMentions}
              onMentionsChange={setPartyAMentions}
              placeholder="@Sarah"
              rows={2}
              bearerToken={bearerToken}
            />
          </Field>

          <Field label="Party B — mention one person with @">
            <MentionAutocomplete
              value={partyBText}
              onChange={setPartyBText}
              mentions={partyBMentions}
              onMentionsChange={setPartyBMentions}
              placeholder="@Marcus"
              rows={2}
              bearerToken={bearerToken}
            />
          </Field>
        </div>

        <Field label="Title (optional — auto-fills from party names)">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Sarah × Marcus · escalated from 1:1"
            style={inputStyle}
          />
        </Field>

        {/*
          Date + duration are optional as of 2026-05-09 (option B re-lock).
          Originally added as required in bug #58 (2026-05-08) to kill
          the lying-header bug. Russ's product call 2026-05-09: forcing
          fake data when the user hasn't scheduled yet recreates the bug.
          The substrate prompt is being re-locked (brainstorming chat)
          to render "Not yet scheduled" in the header line when these
          fields arrive as null.
        */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
          <Field label="When is the mediation scheduled? (optional)">
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              style={inputStyle}
              data-testid="cm-scheduled-at"
            />
          </Field>
          <Field label="Duration in minutes (optional)">
            <input
              type="number"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              min="10"
              max="240"
              step="5"
              placeholder="e.g. 30"
              style={inputStyle}
              data-testid="cm-duration-minutes"
            />
          </Field>
        </div>
        <p style={{
          margin: "-4px 0 12px",
          fontFamily: "var(--ppr-typography-font-family-ui)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          color: "var(--ppr-color-text-muted)",
        }}>
          Leave blank if not yet scheduled — the brief's header will note that and you can finalize the time before convening.
        </p>

        <Field label="What triggered the mediation? (optional)">
          <textarea
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            placeholder="The most recent event that surfaced the conflict — a missed deadline, a hard 1:1, a Slack thread, an escalation from a peer."
            rows={3}
            style={inputStyle}
          />
        </Field>

        <Field label="History — what's been tried already (optional)">
          <textarea
            value={history}
            onChange={(e) => setHistory(e.target.value)}
            placeholder="What's been said, what hasn't, what each party has heard from the other before. This helps the brief read the dynamic, not just the moment."
            rows={3}
            style={inputStyle}
          />
        </Field>

        <Field label="Desired outcome — what would 'this worked' look like? (optional)">
          <textarea
            value={desiredOutcome}
            onChange={(e) => setDesiredOutcome(e.target.value)}
            placeholder="The shape of the conversation you want to leave the room with. Not who's right — what's possible afterward."
            rows={2}
            style={inputStyle}
          />
        </Field>

        <Field label="Tone">
          <div style={{ display: "flex", gap: 8 }}>
            {["hard", "balanced", "soft"].map((v) => (
              <Chip key={v} primary={tone === v} onClick={() => setTone(v)}>{v}</Chip>
            ))}
          </div>
        </Field>

        {/*
          Generation-mode toggle removed 2026-05-07 (bug #55). Streaming-
          vs-sync toggle replaced by Tier 2 background+polling 2026-05-09
          (see /shared/V1_CM_Tier2_Migration_Addendum.md) — generation
          runs in a Background Function with a 15-min cap, the page polls
          for completion. No mode toggle; one path.
        */}

        {error && (
          <div
            style={{
              padding: "12px 14px",
              border: "2px solid var(--ppr-color-accent-coral)",
              background: "var(--ppr-color-accent-coral-very-soft)",
              color: "var(--ppr-color-accent-coral-dark)",
            }}
          >
            {error}
          </div>
        )}

        <button
          onClick={onSubmit}
          disabled={generating}
          style={generating ? terminalActionButtonDisabledStyle : terminalActionButtonStyle}
        >
          {generating ? "Generating brief…" : "Generate the Conflict Mediation Brief"}
        </button>
      </div>
    </div>
  );
}

function RenderedBrief({ blueprint }) {
  const renderCount = useRef(0);
  renderCount.current += 1;
  diag("RenderedBrief.render", {
    render_n: renderCount.current,
    blueprint_id: blueprint?.blueprint_id,
    markdown_chars: blueprint?.blueprint_markdown?.length || 0,
  });
  useEffect(() => {
    diag("RenderedBrief.mounted");
    return () => diag("RenderedBrief.unmounted");
  }, []);
  return (
    <article>
      <SeriesBanner type="Conflict Mediation Brief" dateLabel={new Date().toISOString().slice(0, 10)}/>

      <div style={{ marginTop: 28 }}>
        <h1
          style={{
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-4xl)",
            color: "var(--ppr-color-accent-navy)",
            letterSpacing: "var(--ppr-typography-letter-spacing-tight)",
            lineHeight: "var(--ppr-typography-line-height-tight)",
            margin: "0 0 8px",
          }}
        >
          {blueprint.title}
        </h1>
        {blueprint.power_asymmetry_rendered && (
          <Callout.WatchOut>
            <strong>Power asymmetry:</strong>{" "}
            {blueprint.trap_headline ||
              "One party holds structural power in this pairing. The mediation move that lands tends to be one that surfaces the asymmetry without amplifying it."}
          </Callout.WatchOut>
        )}
      </div>

      {/* MarkdownV2 with subject-name auto-bold (#80). For CM the two
          parties' first names are exposed on the brief envelope — pass
          them through so they're skim-anchored in the prose + tables. */}
      <MarkdownV2
        source={blueprint.blueprint_markdown}
        subjectNames={[blueprint.party_a_first_name, blueprint.party_b_first_name].filter(Boolean)}
      />

      <AuditFooter
        engineVersion={blueprint.engine_version}
        jargonScanPassed={true}
        blueprintId={blueprint.blueprint_id}
        pageIndicator="page 1 of 1"
      />

      {/* Print-to-PDF affordance (#73). The print stylesheet in styles.css
          hides nav + chrome + this button itself when printing. Browser's
          print dialog has "Save as PDF" as the default destination on
          macOS / Windows / Linux Chrome.
          Feature-gate hook lives inside PrintToPdfButton (`disabled`
          prop) — see component for the trial-tier productization plan. */}
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <PrintToPdfButton label="Save as PDF" />
      </div>
    </article>
  );
}

const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
};

function Field({ label, children }) {
  // Nest the input INSIDE the label so the browser implicit-associates
  // them — no need for matching id/htmlFor pairs on every callsite. Fixes
  // the a11y warnings ("form field has no id/name", "label not associated
  // with form field") flagged in DevTools 2026-05-09.
  return (
    <label style={{ display: "block" }}>
      <span
        style={{
          display: "block",
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          color: "var(--ppr-color-text-secondary)",
          marginBottom: 6,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
